import { AdminContext } from "~/contexts/AdminContext"
import ICoupon from "~/interfaces/ICoupon"
import { useContext, useEffect, useState } from "react"
import {
  ACTIVE_DATE_TYPE_NO_EXPIRATION,
  ACTIVE_DATE_TYPE_WITH_EXPIRATION,
  ActiveDatesType,
  activeDatesTypes,
  DISCOUNT_TYPE_AMOUNT_OF_SUBTOTAL,
  DISCOUNT_TYPE_FLAT_RATE,
  DISCOUNT_TYPE_NIGHTLY_AMOUNT_OFF,
  DISCOUNT_TYPE_PERCENT,
  DiscountType,
  discountTypes,
  ExpirationByConstType,
  expirationByOptions,
} from "~/constants/coupons"
import { defineActiveDateType, defineDiscountType, defineExpirationBy } from "~/utils/coupons"
import copyText from "~/utils/copyText"
import { useDebouncedCallback } from "use-debounce"
import api2 from "~/api2"
import parseDateTime from "~/utils/parseDateTime"
import { TicketIcon } from "@heroicons/react/24/solid"
import copy from "../../../../../../assets/images/copy_icons/copyOutline.svg"
import copied from "../../../../../../assets/images/copy_icons/copiedOutline.svg"
import InputFieldWithLabel from "~/components/Admin/Inputs/InputFieldWithLabel"
import classNames from "~/utils/classNames"
import PercentInputWithLabel from "~/components/Admin/Settings/Coupons/PercentInputWithLabel"
import { currencySymbol } from "~/utils/currencySymbol"
import DollarInputWithLabel from "~/components/Admin/Settings/Coupons/DollarInputWithLabel"
import Button from "~/components/ui/Button"
import CouponsSelector from "~/components/Admin/Settings/Coupons/CouponsModalContent/CouponsSelector"
import CouponsActiveDates from "~/components/Admin/Settings/Coupons/CouponsModalContent/CouponsActiveDates"
import { useCoupons } from "~/api2/hooks/useCoupons"
import { useCamp } from "~/api2/hooks/useCamp"
import { useNotification } from "~/contexts/NotificationProvider"
import { useModal } from "~/contexts/ModalProvider"

interface IComponentProps {
  isCreate: boolean
  couponToEdit?: ICoupon

  setIsLoading: (v: boolean) => void
}

const formatCode = (str: string) => {
  if (!str.length) return str
  return str.slice(0, str.length - 1) + str[str.length - 1].replace(/[^\w]|_/g, "").toUpperCase()
}

const CouponsModalContent = ({ isCreate, couponToEdit, setIsLoading }: IComponentProps) => {
  const { showNotification } = useNotification()
  const { closeModal } = useModal()
  const {
    index: { data: camp },
  } = useCamp()
  const { refetch: refetchCoupons } = useCoupons()

  const [isRemoveButtonClicked, setIsRemoveButtonClicked] = useState(false)

  const [coupon, setCoupon] = useState<ICoupon>({ ...couponToEdit } as ICoupon)

  const [copyCodeClicked, setCopyCodeClicked] = useState(false)
  const [couponType, setCouponType] = useState<{ label: string; value: DiscountType }>(
    defineDiscountType(couponToEdit as ICoupon),
  )
  const [activeDateType, setActiveDateType] = useState<{ label: string; value: ActiveDatesType }>(
    defineActiveDateType(couponToEdit as ICoupon),
  )
  const [expirationBy, setExpirationBy] = useState<ExpirationByConstType>(defineExpirationBy(couponToEdit as ICoupon))
  const [errors, setErrors] = useState<Record<keyof ICoupon, string>>()

  const copyCode = () => {
    setCopyCodeClicked(true)
    copyText(coupon?.code || "")
    showNotification({ type: "success", title: "Code copied to clipboard", dismissAfter: 3000, showTimer: true })
  }

  const setValue = <K extends keyof ICoupon>(field: K, value: ICoupon[K]) => {
    setCoupon((prev) => ({ ...prev, [field]: value }))
  }

  const getDiscountDetails = (couponType: { label: string; value: DiscountType }) => {
    switch (couponType.value) {
      case DISCOUNT_TYPE_PERCENT:
        return { value: coupon?.percent_off, setValue: (value: string) => setValue("percent_off", value) }
      case DISCOUNT_TYPE_AMOUNT_OF_SUBTOTAL:
        return {
          value: coupon?.subtotal_amount_off_in_cents,
          setValue: (value: number) => setValue("subtotal_amount_off_in_cents", value),
        }
      case DISCOUNT_TYPE_NIGHTLY_AMOUNT_OFF:
        return {
          value: coupon?.amount_off_in_cents,
          setValue: (value: number) => setValue("amount_off_in_cents", value),
        }
      case DISCOUNT_TYPE_FLAT_RATE:
        return { value: coupon?.flat_rate_in_cents, setValue: (value: number) => setValue("flat_rate_in_cents", value) }

      default:
        return { value: coupon?.percent_off, setValue: (value: string) => setValue("percent_off", value) }
    }
  }

  const discountDetails = getDiscountDetails(couponType)
  const discountValue = discountDetails.value
  const discountSetValue = discountDetails.setValue

  const validateDateFields = () => {
    if (
      !coupon?.valid_from?.length &&
      !coupon?.valid_until?.length &&
      activeDateType.value === ACTIVE_DATE_TYPE_WITH_EXPIRATION
    ) {
      setErrors((prev) => ({ ...prev, valid_from: "can't be blank", valid_until: "can't be blank" }))
      return false
    }

    if (
      !coupon?.valid_from?.length &&
      coupon?.valid_until?.length &&
      activeDateType.value === ACTIVE_DATE_TYPE_WITH_EXPIRATION
    ) {
      setErrors((prev) => ({ ...prev, valid_from: "can't be blank" }))
      return false
    }

    if (
      coupon?.valid_from?.length &&
      !coupon?.valid_until?.length &&
      activeDateType.value === ACTIVE_DATE_TYPE_WITH_EXPIRATION
    ) {
      setErrors((prev) => ({ ...prev, valid_until: "can't be blank" }))
      return false
    }

    return true
  }

  const handleCreate = useDebouncedCallback((coupon: ICoupon, discountType: DiscountType) => {
    setIsLoading(true)
    setErrors(undefined)

    const newCoupon: Partial<ICoupon> = {
      ...coupon,
      percent_off: discountType === DISCOUNT_TYPE_PERCENT ? coupon.percent_off || "0" : null,
      amount_off_in_cents: discountType === DISCOUNT_TYPE_NIGHTLY_AMOUNT_OFF ? coupon.amount_off_in_cents : null,
      subtotal_amount_off_in_cents:
        discountType === DISCOUNT_TYPE_AMOUNT_OF_SUBTOTAL ? coupon.subtotal_amount_off_in_cents : null,
      flat_rate_in_cents: discountType === DISCOUNT_TYPE_FLAT_RATE ? coupon.flat_rate_in_cents : null,
      max_redemptions: coupon.max_redemptions,
      expiration_by: activeDateType.value === ACTIVE_DATE_TYPE_NO_EXPIRATION ? null : expirationBy.value,
      valid_from:
        activeDateType.value === ACTIVE_DATE_TYPE_NO_EXPIRATION
          ? null
          : parseDateTime(coupon.valid_from, camp.timezone, { asMoment: true }).format(),
      valid_until:
        activeDateType.value === ACTIVE_DATE_TYPE_NO_EXPIRATION
          ? null
          : parseDateTime(coupon.valid_until, camp.timezone, { asMoment: true })?.endOf("day")?.format(),
    }

    const isValid = validateDateFields()
    if (isValid) {
      api2.Coupons.create(newCoupon as ICoupon, camp.id)
        .then((res) => {
          refetchCoupons()
          closeModal()
          setIsLoading(false)
        })
        .catch((e) => {
          console.error(e)
          setIsLoading(false)
          setErrors(e.errors)
        })
    } else {
      setIsLoading(false)
    }
  }, 100)

  const handleUpdate = useDebouncedCallback(
    (coupon: ICoupon, discountType: DiscountType, activeDateType: ActiveDatesType) => {
      setIsLoading(true)

      const newCoupon: Partial<ICoupon> = {
        ...coupon,
        valid_from:
          activeDateType === ACTIVE_DATE_TYPE_NO_EXPIRATION
            ? null
            : parseDateTime(coupon.valid_from, camp.timezone, { asMoment: true }).format(),
        valid_until:
          activeDateType === ACTIVE_DATE_TYPE_NO_EXPIRATION
            ? null
            : parseDateTime(coupon.valid_until, camp.timezone, { asMoment: true }).endOf("day").format(),
        percent_off: discountType === DISCOUNT_TYPE_PERCENT ? coupon.percent_off : null,
        amount_off_in_cents: discountType === DISCOUNT_TYPE_NIGHTLY_AMOUNT_OFF ? coupon.amount_off_in_cents : null,
        subtotal_amount_off_in_cents:
          discountType === DISCOUNT_TYPE_AMOUNT_OF_SUBTOTAL ? coupon.subtotal_amount_off_in_cents : null,
        flat_rate_in_cents: discountType === DISCOUNT_TYPE_FLAT_RATE ? coupon.flat_rate_in_cents : null,
        max_redemptions: coupon.max_redemptions,
        expiration_by: activeDateType === ACTIVE_DATE_TYPE_NO_EXPIRATION ? null : expirationBy.value,
      }

      const isValid = validateDateFields()

      if (isValid) {
        api2.Coupons.update(newCoupon as ICoupon)
          .then((res) => {
            refetchCoupons()
            closeModal()
            setIsLoading(false)
          })
          .catch((e) => {
            setIsLoading(false)
            setErrors(e.errors)
          })
      } else {
        setIsLoading(false)
      }
    },
    100,
  )

  const handleDelete = useDebouncedCallback((coupon_id: string) => {
    setIsLoading(true)
    api2.Coupons.remove(coupon_id, camp.id).then(() => {
      setIsLoading(false)
      closeModal()
      refetchCoupons()
    })
  }, 100)

  const handleRemoveClick = () => {
    isRemoveButtonClicked ? handleDelete(coupon.id) : setIsRemoveButtonClicked(true)
  }

  useEffect(() => {
    if (
      coupon?.valid_from &&
      parseDateTime(coupon.valid_from, camp.timezone, { asMoment: true }).isAfter(coupon.valid_until)
    ) {
      setValue("valid_until", null)
    }
  }, [coupon?.valid_from])

  useEffect(() => {
    if (
      coupon?.valid_until &&
      parseDateTime(coupon.valid_until, camp.timezone, { asMoment: true }).isBefore(coupon.valid_from, "day")
    ) {
      setValue("valid_from", null)
    }
  }, [coupon?.valid_until])

  useEffect(() => {
    if (copyCodeClicked) {
      setTimeout(() => setCopyCodeClicked(false), 3000)
    }
  }, [copyCodeClicked])

  useEffect(() => {
    if (activeDateType.value === ACTIVE_DATE_TYPE_NO_EXPIRATION) {
      setValue("valid_from", null)
      setValue("valid_until", null)
      setErrors((prev) => ({ ...prev, valid_from: null, valid_until: null }))
    }
  }, [activeDateType.value])

  useEffect(() => {
    if (coupon?.valid_from || coupon?.valid_until) {
      setActiveDateType(activeDatesTypes.find((type) => type.value === ACTIVE_DATE_TYPE_WITH_EXPIRATION))
    }
  }, [coupon?.valid_from, coupon?.valid_until])

  return (
    <div className="flex flex-col justify-between w-full">
      <div className="flex flex-col gap-3">
        <div className="pr-3 flex items-center gap-3">
          <div className="bg-blue-300 rounded-full py-2 px-1.5 h-9 w-9 flex justify-center items-center">
            <TicketIcon className="text-blue-500 w-5.5" />
          </div>
          <p className="font-semibold text-xl mb-2.5">{isCreate ? "Create" : "Edit"} Coupon</p>
        </div>
        <div className="w-full flex justify-center">
          <div className="flex flex-col gap-3">
            <InputFieldWithLabel
              className={"w-full"}
              label="Name"
              value={coupon?.label}
              onChange={(e) => setValue("label", e.target.value)}
              error={errors?.["label"]?.[0]}
            />
            <div className="">
              <label
                htmlFor="name"
                className={classNames(
                  "block font-semibold text-md text-gray-900 mb-0.5",
                  !!errors?.["code"]?.[0]?.length && "text-red-600",
                )}
              >
                Code
              </label>
              <div className="flex items-center gap-3">
                <InputFieldWithLabel
                  className={"w-full"}
                  value={coupon?.code}
                  onChange={(e) => setValue("code", formatCode(e.target.value))}
                  error={errors?.["code"]?.[0]}
                />
                <img
                  className="w-6 h-6 cursor-pointer"
                  onClick={copyCode}
                  src={copyCodeClicked ? copied : copy}
                  alt="copy"
                />
              </div>
            </div>
            <CouponsSelector
              value={couponType.value}
              setValue={setCouponType}
              label="Discount type"
              options={discountTypes}
            />
            {couponType.value === DISCOUNT_TYPE_PERCENT ? (
              <PercentInputWithLabel
                className={"w-full"}
                label={couponType.label}
                value={discountValue as string}
                prefix="%"
                setValue={discountSetValue as (v: string) => void}
              />
            ) : (
              <DollarInputWithLabel
                className={"w-full"}
                label={couponType.label}
                needsUpdatingFromParent
                value={discountValue as number}
                setValue={discountSetValue as (v: number) => void}
                prefix={currencySymbol(camp?.currency)}
                suffix={camp?.currency.toUpperCase() || "USD"}
              />
            )}
            <InputFieldWithLabel
              label="Max redemptions"
              type="number"
              min={0}
              className={"w-full"}
              value={coupon?.max_redemptions}
              placeholder="Unlimited"
              onChange={(e) => setValue("max_redemptions", Number(e.target.value) === 0 ? "" : e.target.value)}
              error={errors?.["max_redemptions"]?.[0]}
            />
            <div className="flex flex-col gap-3">
              <CouponsSelector
                value={activeDateType.value}
                setValue={setActiveDateType}
                label="Active dates"
                options={activeDatesTypes}
              />
              {activeDateType.value === ACTIVE_DATE_TYPE_WITH_EXPIRATION && (
                <>
                  <CouponsSelector
                    value={expirationBy.value}
                    setValue={setExpirationBy}
                    label="Expiration by"
                    options={expirationByOptions}
                    tooltip={expirationBy.tooltip}
                  />
                  <CouponsActiveDates
                    labels={["Valid from", "Valid until"]}
                    activeDateType={activeDateType.value}
                    startDate={coupon?.valid_from}
                    setStartDate={(value) => setValue("valid_from", value)}
                    endDate={coupon?.valid_until}
                    setEndDate={(value) => setValue("valid_until", value)}
                    startDateError={errors?.["valid_from"]}
                    endDateError={errors?.["valid_until"]}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col xs:flex-row xs:justify-between border-t border-gray-200 pt-5 mt-6">
        <div className="">
          {!isCreate && (
            <Button
              type="button"
              className="text-red-500 underline"
              variant="none"
              children={!isRemoveButtonClicked ? "Remove Coupon" : "Are you sure?"}
              onClick={handleRemoveClick}
            />
          )}
        </div>

        <div className="flex gap-4 mt-3 xs:mt-0">
          <Button
            variant="none"
            children={"Cancel"}
            className="text-gray-475 border border-gray-475"
            onClick={() => closeModal()}
          />
          <Button
            variant="green"
            children={isCreate ? "Create Coupon" : "Update Coupon"}
            className="flex-auto xs:flex-initial"
            type="submit"
            onClick={
              isCreate
                ? () => handleCreate(coupon, couponType.value)
                : () => handleUpdate(coupon, couponType.value, activeDateType.value)
            }
          />
        </div>
      </div>
    </div>
  )
}

export default CouponsModalContent
